import React from "react";
import { Box, Typography, keyframes } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

const spinCw = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const spinCcw = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(-360deg); }
`;
const pulseScale = keyframes`
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.82); }
`;

const BRAND_BLUE = "#00336C";
const BRAND_GREEN = "#648315";

/**
 * Brand-styled loading spinner using dual counter-rotating rings.
 *
 * @param {string}  label    - Optional text displayed below the spinner
 * @param {boolean} compact  - Smaller variant (56px) for modal/panel contexts
 * @param {number}  py       - Override vertical padding (MUI spacing units)
 */
export default function BrandSpinner({ label, compact = false, py }) {
  const outerSize = compact ? 56 : 96;
  const borderWidth = compact ? 2.5 : 3.5;
  const inset = compact ? 10 : 16;
  const innerBorderWidth = compact ? 2 : 3;
  const iconSize = compact ? 16 : 26;
  const defaultPy = compact ? 4 : 8;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: py ?? defaultPy,
        gap: compact ? 1.5 : 3.5,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: outerSize,
          height: outerSize,
          flexShrink: 0,
        }}
      >
        {/* Outer ring — brand blue */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            border: `${borderWidth}px solid rgba(0, 51, 108, 0.12)`,
            borderTopColor: BRAND_BLUE,
            borderRightColor: BRAND_BLUE,
            animation: `${spinCw} 1.1s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
          }}
        />
        {/* Inner ring — brand green */}
        <Box
          sx={{
            position: "absolute",
            inset: inset,
            borderRadius: "50%",
            border: `${innerBorderWidth}px solid rgba(100, 131, 21, 0.14)`,
            borderTopColor: BRAND_GREEN,
            borderLeftColor: BRAND_GREEN,
            animation: `${spinCcw} 0.85s cubic-bezier(0.4, 0, 0.6, 1) infinite`,
          }}
        />
        {/* Center icon */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: `${pulseScale} 2.4s ease-in-out infinite`,
          }}
        >
          <AutoAwesomeIcon sx={{ color: BRAND_GREEN, fontSize: iconSize }} />
        </Box>
      </Box>

      {label ? (
        <Typography
          variant={compact ? "body2" : "h6"}
          sx={{ color: BRAND_BLUE, fontWeight: 700, textAlign: "center" }}
        >
          {label}
        </Typography>
      ) : null}
    </Box>
  );
}
